import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  ChartBarIcon, PlusIcon, PencilSquareIcon, ArchiveBoxIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const ROLES = [
  { value: 'director', label: 'Director' },
  { value: 'admin', label: 'Office Admin' },
  { value: 'teacher', label: 'Full-Time Teacher' },
  { value: 'float', label: 'Part-Time Float' },
  { value: 'cook', label: 'Cook' },
];
const MEASUREMENT_TYPES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'review_periods', label: 'Review periods (30/60/90 day, etc.)' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-time' },
];

export default function GoalTemplatesPage() {
  const [role, setRole] = useState('teacher');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template object
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/goal-templates?role=${role}`);
      setTemplates(data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const archive = async (id) => {
    if (!confirm('Archive this goal template? Existing scores stay; new ones can\'t be added against it.')) return;
    try {
      await api.delete(`/goal-templates/${id}`);
      toast.success('Archived');
      load();
    } catch { toast.error('Archive failed'); }
  };

  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div className="animate-slide-up">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="w-6 h-6" /> Goal Templates
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Define the goals each role is scored against. Scores feed into the staff matrix and
            compensation tiers.
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
          <PlusIcon className="w-4 h-4" /> New goal
        </button>
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {ROLES.map(r => (
          <button key={r.value} onClick={() => setRole(r.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${role === r.value ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No active templates for this role yet. Click "New goal" to add one.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">{cat}</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {list.sort((a, b) => (a.display_order - b.display_order) || a.id - b.id).map(t => (
                  <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3 hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{t.title}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Max {Number(t.max_score).toFixed(1)} · {t.measurement_type}
                        {t.data_source && <> · auto-source: {t.data_source}</>}
                      </p>
                      {t.rubric && (
                        <details className="mt-1">
                          <summary className="text-[11px] text-orange-500 cursor-pointer hover:underline">Rubric</summary>
                          <pre className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{t.rubric}</pre>
                        </details>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setEditing(t)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700" title="Edit">
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => archive(t.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600" title="Archive">
                        <ArchiveBoxIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateModal
          template={editing === 'new' ? { role } : editing}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateModal({ template, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    role: template.role || 'teacher',
    category: template.category || '',
    title: template.title || '',
    rubric: template.rubric || '',
    max_score: template.max_score != null ? String(template.max_score) : '5',
    measurement_type: template.measurement_type || 'monthly',
    period_labels_csv: Array.isArray(template.period_labels) ? template.period_labels.join(', ') : '',
    data_source: template.data_source || '',
    display_order: template.display_order != null ? String(template.display_order) : '0',
    notes: template.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title || !form.category) {
      toast.error('Title and category are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        role: form.role,
        category: form.category.trim(),
        title: form.title.trim(),
        rubric: form.rubric || null,
        max_score: Number(form.max_score) || 5,
        measurement_type: form.measurement_type,
        period_labels: form.period_labels_csv.trim()
          ? form.period_labels_csv.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        data_source: form.data_source || null,
        display_order: Number(form.display_order) || 0,
        notes: form.notes || null,
      };
      if (isNew) {
        await api.post('/goal-templates', payload);
        toast.success('Goal template created');
      } else {
        await api.patch(`/goal-templates/${template.id}`, payload);
        toast.success('Goal template updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{isNew ? 'New goal' : 'Edit goal'}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
              placeholder='e.g. "Class Observation" or "Program Growth"'
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Goal title">
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Rubric (multi-line, e.g. '5=All complete')">
            <textarea value={form.rubric} onChange={(e) => setForm({...form, rubric: e.target.value})}
              rows={4}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y font-mono text-xs" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max score">
              <input type="number" step="0.5" value={form.max_score}
                onChange={(e) => setForm({...form, max_score: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
            <Field label="Measurement type">
              <select value={form.measurement_type} onChange={(e) => setForm({...form, measurement_type: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
                {MEASUREMENT_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Custom period labels (comma-separated, optional)">
            <input value={form.period_labels_csv}
              onChange={(e) => setForm({...form, period_labels_csv: e.target.value})}
              placeholder='e.g. "30 Day, 60 Day, 90 Day, 6 Month, 1 Year"'
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Display order">
            <input type="number" value={form.display_order}
              onChange={(e) => setForm({...form, display_order: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
            <button onClick={save} disabled={saving}
              className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
